/** Wire ⇄ table rows for tag-category associations. The read side takes the generic
 *  `Group_Tour_Tag_Category_M2M` shape every endpoint returns (`{id, category: {id, code,
 *  name}, sequence, min_tags, max_tags, <booleans>}`); the write side emits what the
 *  operator-defaults PUT takes (`{id?, category_id, min_tags, max_tags, <19 flags>}`).
 *
 *  A flag the wire row does not carry reads as off. A host whose endpoint does not take
 *  every flag passes the ones it does to `toWire` — sending a flag the server never showed
 *  would overwrite the stored value (the console tour serializer has no `departure_key`).
 *
 *  A row whose category no longer resolves (`category: null`) cannot be written: the server
 *  requires `category_id`, and leaving the row out of a full-replace list would delete it.
 *  `rowsWithoutCategory` names them so a host blocks its save; `toWire` throws on one. */
import { ALL_FLAGS, type Flag } from './flags';
import type { AssocCategory, AssocOwner, AssocRow, AssocRowState, InheritedRow } from './types';

export type WireAssocRow = {
  id?: number | null;
  category: { id: number; code?: string | null; name?: string | null } | null;
  sequence?: number | null;
  min_tags?: number | null;
  max_tags?: number | null;
} & Partial<Record<Flag, boolean | null>>;

export type WireAssocWrite = {
  id?: number;
  category_id: number;
  min_tags: number;
  max_tags: number;
} & Partial<Record<Flag, boolean>>;

const category = (c: WireAssocRow['category']): AssocCategory | null =>
  c ? { id: c.id, code: c.code ?? null, name: c.name ?? null } : null;

/** A stored row (`key` defaults to its id). */
export function fromWire(
  row: WireAssocRow,
  opts: { key?: string; state?: AssocRowState } = {},
): AssocRow {
  const id = typeof row.id === 'number' ? row.id : undefined;
  return {
    key: opts.key ?? String(id),
    ...(id !== undefined ? { id } : {}),
    category: category(row.category),
    min_tags: row.min_tags ?? 0,
    max_tags: row.max_tags ?? 0,
    flags: Object.fromEntries(ALL_FLAGS.map((f) => [f, row[f] === true])) as Record<Flag, boolean>,
    state: opts.state ?? 'stored',
  };
}

/** A row from the scope above (key `inherited:<id>`, so it never meets an own row's). */
export function inheritedFromWire(
  row: WireAssocRow,
  owner: AssocOwner,
  opts: { key?: string } = {},
): InheritedRow {
  return { ...fromWire(row, { key: opts.key ?? `inherited:${row.id ?? ''}` }), owner };
}

/** The rows a write cannot carry: own rows (not removed) without a category. A host blocks
 *  its save while there are any. */
export const rowsWithoutCategory = (rows: readonly AssocRow[]): AssocRow[] =>
  rows.filter((r) => r.state !== 'removed' && !r.category);

/** One row for a write; `flags` limits the booleans sent (default: all 19). Throws on a row
 *  without a category — never sends a null the server rejects, never drops the row (which
 *  would delete it): the host blocks the save first (`rowsWithoutCategory`). */
export function toWire(row: AssocRow, flags: readonly Flag[] = ALL_FLAGS): WireAssocWrite {
  if (!row.category) {
    throw new Error(`toWire: row ${row.key} has no category — block the save first`);
  }
  const out: WireAssocWrite = {
    ...(row.id !== undefined && row.state !== 'new' ? { id: row.id } : {}),
    category_id: row.category.id,
    min_tags: row.min_tags,
    max_tags: row.max_tags,
  };
  for (const f of flags) out[f] = row.flags[f];
  return out;
}

/** The whole list for a full-replace write, in display order (= sequence): a removed row
 *  is left out, which deletes it. Throws if a row left in has no category (`toWire`). */
export function toWireList(
  rows: readonly AssocRow[],
  flags: readonly Flag[] = ALL_FLAGS,
): WireAssocWrite[] {
  return rows.filter((r) => r.state !== 'removed').map((r) => toWire(r, flags));
}
